import React from 'react';
import { Link } from 'react-router-dom';
import VideoPlayerModal from '../components/conversation/VideoPlayerModal';
import coordIllustration from '../assets/coord-illustration.jpg';
import promoVideo from '../assets/Coord Promo - Landingpage - Sept - optimized.mp4';
import spissLogo from '../assets/partners/spiss-logo.png';
import lukraLogo from '../assets/partners/lukra-veiledning-logo.png';
import digitalAssistLogo from '../assets/partners/digital-assist-logo.png';
import microsoftLogo from '../assets/partners/microsoft-logo.png';
import azureLogo from '../assets/partners/microsoft-azure-logo.png';
import vippsLogo from '../assets/partners/vipps-logo.png';

/**
 * Public landing page for app.coord.no (logged-out visitors on /).
 * Renders inside the app shell — the base surface is the content pane's
 * warm white (bg-background); cream bands (bg-background-card) carry the
 * marquee and pricing sections, same tonal seam as the rest of the app.
 */

// Botanical corner silhouettes for the pricing band — leaf fan in the
// hero illustration's language, filled with currentColor tints.
const CornerLeaves = ({ className }) => (
  <svg viewBox="0 0 200 200" fill="currentColor" className={className} aria-hidden="true">
    <path d="M14 198 C 10 130 22 66 52 26 C 44 92 36 150 30 198 Z" />
    <path d="M36 198 C 52 140 92 92 148 74 C 104 116 72 160 58 200 Z" opacity=".7" />
    <path d="M4 156 C 2 118 8 84 28 58 C 20 96 14 128 12 158 Z" opacity=".55" />
  </svg>
);

// Outline icon set — stroke-based, currentColor. This sets the icon
// direction the rest of the app will follow in a later PR.
const StrokeIcon = ({ className, children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {children}
  </svg>
);

const PARTNERS = [
  { src: spissLogo, alt: 'SPISS' },
  { src: lukraLogo, alt: 'Lukra Veiledning' },
  { src: digitalAssistLogo, alt: 'Digital Assist' },
  { src: microsoftLogo, alt: 'Microsoft' },
  { src: azureLogo, alt: 'Microsoft Azure' },
  { src: vippsLogo, alt: 'Vipps' },
];

const SOKNAD_TYPES = [
  { name: 'Hjelpestønad', available: true },
  { name: 'Ledsagerbevis', available: false },
  { name: 'Omsorgsstønad', available: false },
];

const FEATURES = [
  {
    title: 'Veiledning i rettigheter',
    text: 'Still spørsmål om stønader, rettigheter og hjelpeapparatet – og få svar basert på norske velferdsregler, tilpasset din families situasjon.',
    icon: (
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    ),
  },
  {
    title: 'Søknadshjelp',
    text: 'Svar på enkle spørsmål om hverdagen deres, og få et gjennomarbeidet søknadsutkast du kan sende til Nav.',
    icon: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </>
    ),
  },
  {
    title: 'Ressurser',
    text: 'Guider, skjemaer og offisielle lenker samlet på ett sted – så du slipper å lete deg gjennom ti forskjellige nettsider.',
    icon: (
      <>
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
      </>
    ),
  },
];

// Anonymised excerpts from parent feedback — no names or attribution.
const TESTIMONIALS = [
  'Nå har vi fått innvilget sats 3, og det kom etterbetaling for 7 måneder. Tusen takk for hjelpen!',
  'Fylte inn det som passet for vårt barn, og har nå fått innvilget sats 2 fra Nav.',
  'Jeg brukte den ferdig utfylte søknaden fra Coord da jeg søkte til Nav. Etter ca. tre uker fikk jeg innvilget hjelpestønad. Tommel opp fra meg!',
];

const TRUST_ITEMS = [
  {
    title: 'Basert på offisielle kilder',
    text: 'Veiledningen bygger på Navs egne rundskriv, lover og forskrifter – ikke synsing.',
    icon: (
      <>
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </>
    ),
  },
  {
    title: 'Dataene dine er trygge',
    text: 'Alt lagres sikkert i Norge og Europa – og informasjonen din selges aldri videre.',
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 11.5 11 13.5 15 9.5" />
      </>
    ),
  },
  {
    title: 'AI med faglig grunnlag',
    text: 'Svarene er forankret i norske velferdsregler – ikke i generelle internett-søk.',
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
      </>
    ),
  },
];

const SOCIAL_LINKS = [
  {
    label: 'Coord på Instagram',
    href: 'https://www.instagram.com/coord.no/',
    icon: (
      <>
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </>
    ),
  },
  {
    label: 'Coord på Facebook',
    href: 'https://www.facebook.com/profile.php?id=61591598048896',
    icon: (
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    ),
  },
  {
    label: 'Coord på LinkedIn',
    href: 'https://www.linkedin.com/company/coord-as/',
    icon: (
      <>
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4V9h4v1.5A6 6 0 0 1 16 8z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
      </>
    ),
  },
];

const FOOTER_LINKS = [
  { label: 'Personvern', to: '/privacy' },
  { label: 'Salgsvilkår', to: '/salgsvilkar' },
  { label: 'Kontakt', to: '/contact' },
];

const CTA_CLASSES =
  'inline-flex items-center justify-center min-h-[48px] bg-primary text-white font-semibold text-lg px-8 py-3.5 rounded-lg hover:bg-primary-600 transition-colors shadow-sm active:scale-[0.98]';

// Outlined sibling of CTA_CLASSES — py-3 instead of py-3.5 so the 2px
// border keeps both buttons the same rendered height side by side.
const CTA_OUTLINE_CLASSES =
  'inline-flex items-center justify-center gap-2 min-h-[48px] border-2 border-primary text-primary font-semibold text-lg px-8 py-3 rounded-lg hover:bg-primary-50 transition-colors active:scale-[0.98]';

const Home = ({ isPopupActive, setIsPopupActive }) => {
  const openAuth = () => {
    if (setIsPopupActive) setIsPopupActive(true);
  };

  const openPromoVideo = () => {
    window.dispatchEvent(
      new CustomEvent('open-video-modal', {
        detail: {
          videoUrl: promoVideo,
          title: 'Se hvordan Coord fungerer',
          size: 'full',
        },
      })
    );
  };

  return (
    <div>
      {/* Width-capped content; only the marquee bleeds across the full pane */}
      <div className="max-w-5xl mx-auto">
      {/* HERO — on the warm-white base, melting into the shell */}
      <section className="pt-4 pb-14 md:pt-12 md:pb-24">
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-10 md:gap-12 items-center">
          <div className="text-center md:text-left">
            <h1 className="text-4xl lg:text-display-lg font-semibold leading-tight text-text-primary opacity-0 animate-landing-fade-up">
              Har barnet ditt fått en diagnose?{' '}
              <span className="block mt-2 text-2xl sm:text-3xl lg:text-display-sm italic font-medium text-sage-700">
                Finn ut hva dere kan ha rett på.
              </span>
            </h1>
            <p className="mt-5 text-lg text-text-secondary leading-relaxed max-w-xl mx-auto md:mx-0 opacity-0 animate-landing-fade-up [animation-delay:100ms]">
              Barn med autisme, ADHD eller annen nevrodivergens kan ha
              rett på støtte fra Nav. Coord gir deg veiledning i rettighetene
              deres, oversikt over aktuelle stønader – og et ferdig utkast til
              søknad om hjelpestønad.
            </p>
            <div className="mt-8 flex flex-col items-center md:items-start gap-3 opacity-0 animate-landing-fade-up [animation-delay:180ms]">
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button type="button" onClick={openAuth} className={CTA_CLASSES}>
                  Kom i gang
                </button>
                <button
                  type="button"
                  onClick={openPromoVideo}
                  className={CTA_OUTLINE_CLASSES}
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 5.5a1 1 0 0 1 1.52-.85l9.5 6.5a1 1 0 0 1 0 1.7l-9.5 6.5A1 1 0 0 1 8 18.5v-13z" />
                  </svg>
                  Se video
                </button>
              </div>
              <button
                type="button"
                onClick={openAuth}
                className="min-h-[48px] px-2 text-sm text-text-muted hover:text-text-primary transition-colors"
              >
                Allerede bruker?{' '}
                <span className="underline underline-offset-2">Logg inn</span>
              </button>
            </div>
          </div>
          <div className="flex justify-center opacity-0 animate-landing-fade [animation-delay:200ms]">
            <img
              src={coordIllustration}
              alt="Illustrasjon av to foreldre som holder et barn i hendene"
              className="w-64 h-64 sm:w-72 sm:h-72 lg:w-96 lg:h-96 rounded-full object-cover"
            />
          </div>
        </div>
      </section>
      </div>

      {/* PARTNER MARQUEE — full-bleed across the content pane, on the base
          surface; negative margins escape the pane's p-4/p-8 padding */}
      <section aria-label="Våre partnere" className="-mx-4 md:-mx-8">
        <p className="text-center text-xs font-medium uppercase tracking-widest text-text-muted mb-8">
          Våre partnere
        </p>
        <div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)] [-webkit-mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
          <div className="flex w-max animate-marquee motion-reduce:animate-none">
            {[false, true].map((isDuplicate) => (
              <ul
                key={isDuplicate ? 'duplicate' : 'original'}
                aria-hidden={isDuplicate || undefined}
                className="flex items-center gap-16 md:gap-24 pr-16 md:pr-24"
              >
                {PARTNERS.map(({ src, alt }) => (
                  <li key={alt} className="shrink-0">
                    <img
                      src={src}
                      alt={isDuplicate ? '' : alt}
                      loading="lazy"
                      className="h-16 md:h-24 w-auto grayscale opacity-70 mix-blend-multiply select-none"
                    />
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto">
      {/* FEATURES — cream cards on the warm-white base, app card idiom */}
      <section className="py-14 md:py-24">
        <h2 className="text-2xl md:text-3xl font-semibold text-center text-text-primary mb-10 md:mb-12">
          Hva Coord hjelper deg med
        </h2>
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {FEATURES.map(({ title, text, icon }) => (
            <div
              key={title}
              className="bg-white border border-border-light rounded-2xl p-6 md:p-7 shadow-xs"
            >
              <StrokeIcon className="w-7 h-7 text-primary-600 mb-4">
                {icon}
              </StrokeIcon>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {title}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                {text}
              </p>
            </div>
          ))}
        </div>
        {/* Søknadstype status — the badges get their own stage */}
        <div className="mt-7 md:mt-9 text-center">
          <p className="text-sm text-text-muted mb-3">Søknadshjelperen dekker:</p>
          <ul className="flex flex-wrap justify-center gap-2.5">
            {SOKNAD_TYPES.map(({ name, available }) => (
              <li
                key={name}
                className={`flex items-center gap-2.5 rounded-full border px-4 py-2.5 ${
                  available
                    ? 'border-primary-300 bg-primary-100'
                    : 'border-border-light bg-white'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    available
                      ? 'bg-primary-500 animate-pulse-gentle'
                      : 'bg-neutral-300'
                  }`}
                />
                <span className="text-sm font-medium text-text-primary">
                  {name}
                </span>
                <span
                  className={`text-xs font-semibold ${
                    available ? 'text-primary-800' : 'text-text-muted'
                  }`}
                >
                  {available ? 'Tilgjengelig nå' : 'Kommer snart'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TESTIMONIALS — quiet quotes on the base surface, sage left rule
          instead of cards so they don't read as a second features row */}
      <section aria-labelledby="testimonials-heading" className="pb-14 md:pb-24">
        <h2
          id="testimonials-heading"
          className="text-2xl md:text-3xl font-semibold text-center text-balance text-text-primary mb-3"
        >
          Fra foreldre som har brukt Coord
        </h2>
        <p className="font-display text-sm italic text-text-muted text-center text-balance max-w-xl mx-auto mb-10 md:mb-12">
          Utdrag fra tilbakemeldinger vi har fått på e-post og andre
          plattformer, anonymisert for personvern.
        </p>
        <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
          {TESTIMONIALS.map((quote) => (
            <figure key={quote} className="border-l-2 border-sage-300 pl-4">
              <blockquote className="font-display text-lg italic text-text-primary leading-relaxed">
                {quote}
              </blockquote>
            </figure>
          ))}
        </div>
      </section>

      {/* PRICING — warm, lush band in the hero illustration's palette:
          peach/sage gradient, soft colour pools, botanical corners */}
      <section aria-labelledby="pricing-heading">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-50 via-background-warm to-sage-100 px-4 py-12 md:py-16">
          <div
            aria-hidden="true"
            className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-primary-200/50 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-24 -left-20 w-80 h-80 rounded-full bg-sage-200/60 blur-3xl"
          />
          <CornerLeaves className="absolute -bottom-3 -left-2 w-40 h-40 md:w-56 md:h-56 text-sage-300/80" />
          <CornerLeaves className="absolute -top-3 -right-2 w-36 h-36 md:w-48 md:h-48 rotate-180 text-primary-200/90" />
          <div className="relative">
          <h2
            id="pricing-heading"
            className="text-2xl md:text-3xl font-semibold text-center text-text-primary mb-8 md:mb-10"
          >
            Én pris. Alt inkludert.
          </h2>
          <div className="max-w-sm mx-auto bg-background-white rounded-2xl border border-border-light shadow-lg p-7 sm:p-8 text-center">
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="font-display text-5xl font-semibold text-text-primary">
                299 kr
              </span>
              <span className="text-lg text-text-muted">/ måned</span>
            </div>
            <ul className="mt-6 w-fit mx-auto text-left space-y-2.5">
              {[
                'Ingen bindingstid',
                'Avslutt når som helst',
                'Alle fremtidige funksjoner inkludert',
                'Inkl. mva.',
              ].map(
                (item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2.5 text-sm text-text-primary"
                  >
                    <StrokeIcon className="w-4.5 h-4.5 text-sage-600 shrink-0">
                      <polyline points="20 6 9 17 4 12" />
                    </StrokeIcon>
                    {item}
                  </li>
                )
              )}
            </ul>
            <button
              type="button"
              onClick={openAuth}
              className={`${CTA_CLASSES} w-full mt-8`}
            >
              Kom i gang
            </button>
          </div>
          </div>
        </div>
      </section>

      {/* TRUST — quiet, on the base surface, sage outline icons */}
      <section className="py-14 md:py-24">
        <h2 className="text-2xl md:text-3xl font-semibold text-center text-text-primary mb-10 md:mb-12">
          Derfor kan du stole på Coord
        </h2>
        <div className="grid md:grid-cols-3 gap-10 md:gap-8 text-center">
          {TRUST_ITEMS.map(({ title, text, icon }) => (
            <div key={title}>
              <StrokeIcon className="w-7 h-7 text-sage-600 mx-auto mb-4">
                {icon}
              </StrokeIcon>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {title}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed max-w-xs mx-auto">
                {text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="pb-14 md:pb-20 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-text-primary mb-4">
          Du er ikke alene om dette.
        </h2>
        <p className="text-lg text-text-secondary leading-relaxed max-w-xl mx-auto mb-8">
          Mange familier går glipp av støtte de har rett på, fordi systemet er
          vanskelig å navigere. Coord viser deg veien – steg for steg.
        </p>
        <button type="button" onClick={openAuth} className={CTA_CLASSES}>
          Kom i gang
        </button>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border-light py-8 md:py-10">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex items-center gap-2">
            {SOCIAL_LINKS.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="p-3 rounded-full text-text-muted hover:text-primary-600 transition-colors"
              >
                <StrokeIcon className="w-6 h-6">{icon}</StrokeIcon>
              </a>
            ))}
          </div>
          <nav
            aria-label="Bunnmeny"
            className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm"
          >
            {FOOTER_LINKS.map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                className="inline-flex items-center min-h-[44px] px-2 text-text-secondary hover:text-text-primary underline-offset-2 hover:underline transition-colors"
              >
                {label}
              </Link>
            ))}
          </nav>
          <p className="text-sm text-text-detail">
            Coord AS · Org.nr: 933 971 376 · Askim, Norge
          </p>
        </div>
      </footer>
      </div>

      <VideoPlayerModal />
    </div>
  );
};

export default Home;
